import AuthModal from '@/components/AuthModal';
import FirstTimeQuerySelection from '@/components/FirstTimeQuerySelection';
import AnalysisProgress from '@/components/AnalysisProgress';
import { useAuth } from '@/context/AuthContext';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Loader2 } from 'lucide-react';
import { LLM_PROVIDER_LOGOS, LlmProvider } from '@/types/llm-provider';
import { llmsService } from '@/api/llmsService';
import { queriesService } from '@/api/queriesService';
import { websiteService } from '@/api/websiteService';
import isURL from 'validator/es/lib/isURL';

const llmLogoImages = [
  LLM_PROVIDER_LOGOS[LlmProvider.ChatGPT],
  LLM_PROVIDER_LOGOS[LlmProvider.Claude],
  LLM_PROVIDER_LOGOS[LlmProvider.Gemini],
  LLM_PROVIDER_LOGOS[LlmProvider.Grok],
  LLM_PROVIDER_LOGOS[LlmProvider.Perplexity],
  LLM_PROVIDER_LOGOS[LlmProvider.DeepSeek],
];

const urlFormSchema = z.object({
  websiteUrl: z
    .string()
    .min(1, { message: "Please enter a website URL" })
    .refine(
      (val) => {
        const urlToTest = val.startsWith('http://') || val.startsWith('https://')
          ? val
          : `https://${val}`;
        return isURL(urlToTest, {
          protocols: ['http', 'https'],
          require_protocol: true,
          require_tld: false,
        });
      },
      { message: "Please enter a valid URL (e.g., https://example.com)" }
    )
});

type UrlFormData = z.infer<typeof urlFormSchema>;

const DemoPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userSession } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [showQuerySelection, setShowQuerySelection] = useState(false);
  const [isPinging, setIsPinging] = useState(false);
  const [pingError, setPingError] = useState<string | null>(null);
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UrlFormData>({
    resolver: zodResolver(urlFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      websiteUrl: ''
    }
  });

  const handleGetStarted = async (data: UrlFormData) => {
    let url = data.websiteUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }

    setPingError(null);
    setIsPinging(true);

    try {
      const pingResult = await llmsService.pingUrl(url);
      if (!pingResult.reachable) {
        if (pingResult.error?.includes('timeout')) {
          setPingError('Website took too long to respond. Please check if the URL is correct.');
        } else if (pingResult.status === 404) {
          setPingError('Website not found. Please check if the URL is correct.');
        } else if (pingResult.status && pingResult.status >= 500) {
          setPingError('Website is temporarily unavailable. Please try again later.');
        } else {
          setPingError('Could not reach this website. Please check if the URL is correct.');
        }
        return;
      }
    } catch {
      setPingError('Could not verify website. Please check your connection and try again.');
      return;
    } finally {
      setIsPinging(false);
    }

    setWebsiteUrl(url);

    if (!isAuthenticated) {
      setAuthOpen(true);
    } else {
      setShowQuerySelection(true);
    }
  };

  const onSubmitForm = handleSubmit(handleGetStarted);

  const handleAuthSuccess = () => {
    setAuthOpen(false);

    if (userSession?.hasResources) {
      navigate('/dashboard');
    } else {
      setShowQuerySelection(true);
    }
  };

  const handleRunAnalysis = async (queries: string[]) => {
    setIsRunningAnalysis(true);
    try {
      const resource = await websiteService.findOrCreateWebsite(websiteUrl);

      await queriesService.testQueries({
        queries,
        targetDomain: websiteUrl,
        resourceId: resource.id,
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Error running analysis:', error);
      navigate('/dashboard');
    }
  };

  if (isRunningAnalysis) {
    return <AnalysisProgress />;
  }

  if (showQuerySelection) {
    return (
      <FirstTimeQuerySelection
        websiteUrl={websiteUrl}
        onContinue={handleRunAnalysis}
      />
    );
  }

  return (
    <div className="relative overflow-hidden h-full">
      <div className="max-w-[1440px] mx-auto px-4 md:px-10 relative z-10">
        <div className="flex flex-col gap-10 items-center pt-6 md:pt-10 pb-10 md:pb-16">
          <div className="flex flex-col gap-8 items-center w-full max-w-[720px]">
            <div className="flex flex-col gap-3 items-start text-foreground text-center w-full max-w-[640px]">
              <h1 className="font-condensed font-bold leading-[1.1] text-[28px] md:text-[32px] uppercase w-full">
                Make sure that AI says the right things about your business. Not the wrong things.
              </h1>
              <p className="font-sans font-normal leading-[1.5] opacity-70 text-base w-full">
                See how LLMs like ChatGPT answer questions related to your business, and create an LLMs.txt file to make sure they say the right things.
              </p>
            </div>

            <div className="bg-card border border-border rounded px-6 py-7 w-full">
              <form onSubmit={onSubmitForm} className="flex flex-col gap-6">
                <div className="flex flex-col gap-5 justify-center">
                  <div className="flex flex-col gap-3">
                    <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                      How it works
                    </p>
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-3 items-center">
                        <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                          <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">
                            1
                          </p>
                        </div>
                        <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                          Enter your website URL
                        </p>
                      </div>
                      <div className="flex gap-3 items-center">
                        <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                          <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">
                            2
                          </p>
                        </div>
                        <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                          Check what AI says about you
                        </p>
                      </div>
                      <div className="flex gap-3 items-center">
                        <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                          <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">
                            3
                          </p>
                        </div>
                        <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                          Generate an LLMs.txt so AI says the right things
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                      WEBSITE URL
                    </p>
                    <input
                      type="text"
                      {...register('websiteUrl', {
                        onChange: (e) => setWebsiteUrl(e.target.value)
                      })}
                      placeholder="https://mywebsite.com"
                      className={`border border-border rounded p-3 font-sans font-normal leading-[1.5] text-foreground text-base bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${errors.websiteUrl ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {(errors.websiteUrl || pingError) && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.websiteUrl?.message || pingError}
                      </p>
                    )}
                    {!errors.websiteUrl && !pingError && (
                      <p className="font-sans italic leading-[1.5] opacity-50 text-foreground text-sm text-right">
                        We'll run queries across the most popular LLMs to see whether your site is mentioned.
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isPinging}
                  className="bg-primary border border-foreground rounded flex h-14 items-center justify-center p-3 w-full hover:opacity-90 transition-opacity disabled:opacity-70"
                >
                  {isPinging ? (
                    <Loader2 className="h-5 w-5 text-white animate-spin" />
                  ) : (
                    <p className="font-mono font-medium leading-[1.5] text-sm text-center text-white tracking-[0.7px] uppercase">
                      Get Started for Free
                    </p>
                  )}
                </button>
              </form>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-between w-full gap-4">
              <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase text-center md:text-left w-full md:w-auto md:max-w-[400px]">
                Works with chatgpt, claude, gemini, grok, perplexity, Deepseek, and more.
              </p>

              <div className="flex items-start pr-3">
                {llmLogoImages.map((logo, index) => (
                  <div
                    key={index}
                    className="bg-white border border-border rounded-full overflow-hidden w-10 h-10 md:w-12 md:h-12 -mr-3 flex items-center justify-center"
                    style={{ zIndex: llmLogoImages.length - index }}
                  >
                    <img
                      src={logo}
                      alt={`LLM ${index + 1}`}
                      className="w-6 h-6 md:w-8 md:h-8 object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        onSuccess={handleAuthSuccess}
        defaultTab="register"
        websiteUrl={websiteUrl}
      />
    </div>
  );
};

export default DemoPage;
